import { FormEvent, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import api from "../../utils/api";
import { isUrisBrand } from "../../utils/brandLogo";
import { useAppSelector } from "../../store/hooks";

type ClientRow = {
  user_id: number;
  user_name: string;
  token: string;
  email: string;
  balance: number;
};

type PackageRow = {
  id: number;
  package_name: string;
  token_count: number;
  is_active: boolean;
};

type LedgerRow = {
  id: number;
  user_id: number;
  delta: number;
  balance_after: number;
  reason: string;
  note: string | null;
  case_id: string | null;
  created_at: string;
};

const UrisManualTokens = () => {
  const { user } = useAppSelector((state) => state.auth);
  const allowed = isUrisBrand(user) && user?.role === "Side Admin";
  const [clients, setClients] = useState<ClientRow[]>([]);
  const [packages, setPackages] = useState<PackageRow[]>([]);
  const [ledger, setLedger] = useState<LedgerRow[]>([]);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<number | "">("");
  const [packageId, setPackageId] = useState<number | "">("");
  const [tokens, setTokens] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async (query = search) => {
    const [clientRes, packageRes, ledgerRes] = await Promise.all([
      api.get("/api/uris-tokens/clients", { params: { search: query } }),
      api.get("/api/uris-tokens/packages"),
      api.get("/api/uris-tokens/ledger"),
    ]);
    setClients(clientRes.data?.data || []);
    setPackages(
      (packageRes.data?.data || []).filter((row: PackageRow) => row.is_active),
    );
    setLedger(ledgerRes.data?.data || []);
  };

  useEffect(() => {
    if (!allowed) return;
    load("").catch(() => setError("Could not load clients."));
  }, [allowed]);

  const assign = async (event: FormEvent) => {
    event.preventDefault();
    if (!selectedId) return;
    setSaving(true);
    setError("");
    try {
      if (packageId) {
        await api.post("/api/uris-tokens/assign-package", {
          user_id: selectedId,
          package_id: packageId,
          note,
        });
      } else {
        await api.post("/api/uris-tokens/assign", {
          user_id: selectedId,
          tokens: Number(tokens),
          note,
        });
      }
      setTokens("");
      setNote("");
      setPackageId("");
      await load();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "Could not update tokens.";
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  if (!allowed) {
    return (
      <p className="text-[#c5c7cc]">
        Manual tokens are only available inside the Uris side admin account.
      </p>
    );
  }

  const clientName = (userId: number) =>
    clients.find((client) => client.user_id === userId)?.user_name || `#${userId}`;

  return (
    <>
      <Helmet>
        <title>Manual Token</title>
      </Helmet>
      <div className="text-white">
        <h1 className="text-2xl font-semibold">Manual Token</h1>
        <p className="mt-1 text-sm text-[#9a9ca3]">
          Add or remove tokens for a Uris lab. One token is used each time that
          lab creates a case.
        </p>

        <form
          onSubmit={assign}
          className="mt-4 grid gap-2 rounded-2xl border border-[#6b2430] bg-[#141517] p-4 md:grid-cols-2"
        >
          <label className="flex flex-col gap-1 text-xs text-[#9a9ca3]">
            Client
            <select
              value={selectedId}
              onChange={(e) =>
                setSelectedId(e.target.value ? Number(e.target.value) : "")
              }
              className="rounded-xl border border-[#3a3c42] bg-[#1a1b1f] px-3 py-2 text-sm text-white outline-none"
            >
              <option value="">Select a lab</option>
              {clients.map((client) => (
                <option key={client.user_id} value={client.user_id}>
                  {client.user_name} ({client.token}) — {client.balance} tokens
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-[#9a9ca3]">
            Package
            <select
              value={packageId}
              onChange={(e) =>
                setPackageId(e.target.value ? Number(e.target.value) : "")
              }
              className="rounded-xl border border-[#3a3c42] bg-[#1a1b1f] px-3 py-2 text-sm text-white outline-none"
            >
              <option value="">Custom amount</option>
              {packages.map((pack) => (
                <option key={pack.id} value={pack.id}>
                  {pack.package_name} (+{pack.token_count})
                </option>
              ))}
            </select>
          </label>
          {!packageId ? (
            <label className="flex flex-col gap-1 text-xs text-[#9a9ca3]">
              Tokens to add or remove
              <input
                value={tokens}
                onChange={(e) => setTokens(e.target.value.replace(/[^\d-]/g, ""))}
                className="rounded-xl border border-[#3a3c42] bg-[#1a1b1f] px-3 py-2 text-sm text-white outline-none focus:border-[#c31c30]"
                placeholder="10 or -2"
              />
            </label>
          ) : null}
          <label className="flex flex-col gap-1 text-xs text-[#9a9ca3]">
            Note
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="rounded-xl border border-[#3a3c42] bg-[#1a1b1f] px-3 py-2 text-sm text-white outline-none focus:border-[#c31c30]"
              placeholder="Optional"
            />
          </label>
          <div className="md:col-span-2">
            <button
              type="submit"
              disabled={saving || !selectedId}
              className="rounded-xl bg-[#e11d2e] px-4 py-2 text-sm font-medium text-white hover:bg-[#c31c30] disabled:opacity-60"
            >
              {saving ? "Saving…" : "Assign tokens"}
            </button>
          </div>
        </form>
        {error ? <p className="mt-3 text-sm text-[#ff8b96]">{error}</p> : null}

        <div className="mt-4 flex flex-col gap-2 md:flex-row">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, token, or email"
            className="flex-1 rounded-xl border border-[#3a3c42] bg-[#141517] px-3 py-2 text-sm text-white outline-none focus:border-[#c31c30]"
          />
          <button
            type="button"
            onClick={() => load(search)}
            className="rounded-xl border border-[#3a3c42] px-4 py-2 text-sm text-[#c5c7cc] hover:border-[#c31c30]"
          >
            Search
          </button>
        </div>

        <div className="mt-4 overflow-x-auto rounded-2xl border border-[#2e3036] bg-[#141517]">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-[#8b8d93]">
                <th className="px-4 py-3">Lab</th>
                <th className="px-4 py-3">Token</th>
                <th className="px-4 py-3">Balance</th>
              </tr>
            </thead>
            <tbody>
              {clients.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-4 py-8 text-center text-[#8b8d93]">
                    No Uris clients found.
                  </td>
                </tr>
              ) : (
                clients.map((client) => (
                  <tr key={client.user_id} className="border-t border-[#2e3036]">
                    <td className="px-4 py-3">
                      <div className="text-white">{client.user_name}</div>
                      <div className="text-xs text-[#8b8d93]">{client.email}</div>
                    </td>
                    <td className="px-4 py-3 text-[#c5c7cc]">{client.token}</td>
                    <td className="px-4 py-3 font-medium text-[#ff8b96]">
                      {client.balance}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <h2 className="mt-6 text-sm font-semibold uppercase tracking-wide text-[#8b8d93]">
          Recent changes
        </h2>
        <div className="mt-2 overflow-x-auto rounded-2xl border border-[#2e3036] bg-[#141517]">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-[#8b8d93]">
                <th className="px-4 py-3">When</th>
                <th className="px-4 py-3">Lab</th>
                <th className="px-4 py-3">Change</th>
                <th className="px-4 py-3">After</th>
                <th className="px-4 py-3">Why</th>
              </tr>
            </thead>
            <tbody>
              {ledger.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-[#8b8d93]">
                    No token changes yet.
                  </td>
                </tr>
              ) : (
                ledger.map((row) => (
                  <tr key={row.id} className="border-t border-[#2e3036] text-[#c5c7cc]">
                    <td className="px-4 py-3 whitespace-nowrap">
                      {new Date(row.created_at).toLocaleString("en-GB")}
                    </td>
                    <td className="px-4 py-3">{clientName(row.user_id)}</td>
                    <td
                      className={`px-4 py-3 ${
                        row.delta < 0 ? "text-[#ff8b96]" : "text-[#7dffa8]"
                      }`}
                    >
                      {row.delta > 0 ? `+${row.delta}` : row.delta}
                    </td>
                    <td className="px-4 py-3">{row.balance_after}</td>
                    <td className="px-4 py-3">
                      {row.note || row.reason}
                      {row.case_id ? ` · ${row.case_id}` : ""}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default UrisManualTokens;
